import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCampaignById, updateCampaignThunk, clearSelectedCampaign } from "../../../Redux/campaignSlice";
import { FaArrowLeft } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EditCampaign = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedCampaign, loading, error } = useSelector((state) => state.campaign);

  useEffect(() => {
    dispatch(getCampaignById(id));

    return () => {
      dispatch(clearSelectedCampaign());
    };
  }, [dispatch, id]);

  // Ensure commission model is correct for Advertising campaigns
  useEffect(() => {
    if (selectedCampaign && selectedCampaign.campaignType === "Advertising" && selectedCampaign.commission?.model !== "fixed") {
      const newCommission = {
        ...selectedCampaign.commission,
        model: "fixed"
      };
      dispatch({
        type: "campaign/updateSelectedCampaign",
        payload: { commission: newCommission }
      });
    }
  }, [selectedCampaign, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateCampaignThunk({ id, data: selectedCampaign })).unwrap();
      toast.success("Campaign updated successfully");
      navigate("/company/dashboard/campaigns/");
    } catch (error) {
      toast.error(error?.message || "Failed to update campaign");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If campaign type is changed to "Advertising", ensure commission model is "fixed"
    if (name === "campaignType" && value === "Advertising") {
      const newCommission = {
        ...selectedCampaign.commission,
        model: "fixed"
      };
      dispatch({
        type: "campaign/updateSelectedCampaign",
        payload: { 
          [name]: value,
          commission: newCommission
        }
      });
    } else {
      dispatch({
        type: "campaign/updateSelectedCampaign",
        payload: { [name]: value }
      });
    }
  };

  const handleCommissionChange = (e) => {
    const { name, value } = e.target;
    const newCommission = {
      ...selectedCampaign.commission,
      [name]: value
    };
    dispatch({
      type: "campaign/updateSelectedCampaign",
      payload: { commission: newCommission }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!selectedCampaign) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600">Campaign not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <ToastContainer />
      <div className="mb-6">
        <button
          onClick={() => navigate("/company/dashboard/campaigns/")}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200"
        >
          <FaArrowLeft className="mr-2" /> Back to Campaigns
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-10 lg:mb-0">
        <h1 className="text-2xl font-bold mb-6">Edit Campaign</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={selectedCampaign.name || ""}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                required
              />
            </div>

            <div>
              <label htmlFor="campaignType" className="block text-sm font-medium text-gray-700">
                Campaign Type
              </label>
              <select
                id="campaignType"
                name="campaignType"
                value={selectedCampaign.campaignType || ""}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                required
              >
                <option value="">Select Type</option>
                <option value="Advertising">Advertising</option>
                <option value="Affiliate">Affiliate</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={selectedCampaign.status || ""}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                required
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={selectedCampaign.description || ""}
                onChange={handleChange}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                required
              />
            </div>

            <div>
              <label htmlFor="landingPage" className="block text-sm font-medium text-gray-700">
                Landing Page URL
              </label>
              <input
                type="url"
                id="landingPage"
                name="landingPage"
                value={selectedCampaign.landingPage || ""}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                required
              />
            </div>

            <div>
              <label htmlFor="commissionModel" className="block text-sm font-medium text-gray-700">
                Commission Model
              </label>
              <select
                id="commissionModel"
                name="model"
                value={selectedCampaign.commission?.model || ""}
                onChange={handleCommissionChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                required
              >
                <option value="">Select Model</option>
                <option value="fixed">Fixed</option>
                {selectedCampaign.campaignType !== "Advertising" && (
                  <option value="percentage">Percentage</option>
                )}
              </select>
              {/* {selectedCampaign.campaignType === "Advertising" && (
                <p className="mt-1 text-sm text-blue-600">
                  Advertising campaigns only support Fixed commission model
                </p>
              )} */}
            </div>

            {selectedCampaign.commission?.model === "fixed" ? (
              <div>
                <label htmlFor="commissionAmount" className="block text-sm font-medium text-gray-700">
                  Commission Amount
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">
                      {selectedCampaign.commission?.currency?.symbol || "$"}
                    </span>
                  </div>
                  <input
                    type="number"
                    id="commissionAmount"
                    name="amount"
                    value={selectedCampaign.commission?.amount || ""}
                    onChange={handleCommissionChange}
                    className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="commissionPercentage" className="block text-sm font-medium text-gray-700">
                  Commission Percentage
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="number"
                    id="commissionPercentage"
                    name="percentage"
                    value={selectedCampaign.commission?.percentage || ""}
                    onChange={handleCommissionChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
              </div>
            )}

            {/* CPE and CPM for Advertising and Hybrid Campaigns with Fixed Model */}
            {((selectedCampaign.campaignType === "Advertising" || selectedCampaign.campaignType === "Hybrid") && 
              selectedCampaign.commission?.model === "fixed") && (
              <>
                <div>
                  <label htmlFor="cpe" className="block text-sm font-medium text-gray-700">
                    CPE (Cost Per Engagement)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">
                        {selectedCampaign.commission?.currency?.symbol || "$"}
                      </span>
                    </div>
                    <input
                      type="number"
                      id="cpe"
                      name="cpe"
                      value={selectedCampaign.commission?.cpe || ""}
                      onChange={handleCommissionChange}
                      className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="cpm" className="block text-sm font-medium text-gray-700">
                    CPM (Cost Per Thousand Views)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">
                        {selectedCampaign.commission?.currency?.symbol || "$"}
                      </span>
                    </div>
                    <input
                      type="number"
                      id="cpm"
                      name="cpm"
                      value={selectedCampaign.commission?.cpm || ""}
                      onChange={handleCommissionChange}
                      className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {/* CPE and CPM for Hybrid Campaigns with Percentage Model */}
            {(selectedCampaign.campaignType === "Hybrid" && 
              selectedCampaign.commission?.model === "percentage") && (
              <>
                <div>
                  <label htmlFor="cpe" className="block text-sm font-medium text-gray-700">
                    CPE (Cost Per Engagement)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">
                        {selectedCampaign.commission?.currency?.symbol || "$"}
                      </span>
                    </div>
                    <input
                      type="number"
                      id="cpe"
                      name="cpe"
                      value={selectedCampaign.commission?.cpe || ""}
                      onChange={handleCommissionChange}
                      className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="cpm" className="block text-sm font-medium text-gray-700">
                    CPM (Cost Per Thousand Views)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">
                        {selectedCampaign.commission?.currency?.symbol || "$"}
                      </span>
                    </div>
                    <input
                      type="number"
                      id="cpm"
                      name="cpm"
                      value={selectedCampaign.commission?.cpm || ""}
                      onChange={handleCommissionChange}
                      className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/company/dashboard/campaigns/")}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCampaign;
